import Button from '@/components/UI/Button';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { renderMetadataValue, successToast } from '@/helpers';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField, MetadataPostItem } from '@/model/Metadata.model';
import type {
  BulkSamplesFormData,
  SampleData,
  SampleDetailKey,
  SampleDetailUnit,
  SampleDetailV1,
  SampleDetailValue,
  SampleMeasurementKey,
} from '@/model/Sample.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import { type FC, useRef, useState } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { formatColumnSettingsDataValue } from '@/support/hot';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { BrowserRouter, Link } from 'react-router-dom';
import { web } from '@/support/route.ts';
import { RouteService } from '@/support/RouteService';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

import ViewBulkSampleDetailsTable from './ViewBulkSampleDetailsTable';
import styles from './ViewBulkSampleDetails.module.scss';

interface ViewBulkSampleDetailsProps {
  studyId: Study['id'];
  studyApiId: StudyApiId;
  subjects: Array<Animal>;
  closeModal: () => void;
  handleCallback: (finished: boolean, viewSamples: boolean) => void;
  sampleDetails: BulkSamplesFormData;
  metadata?: Array<MetadataField>;
}
const featureSelector = createSelector([featuresSelector], (features) => ({
  plannedSamplesEnabled: features?.planned_samples ?? false,
}));
const ViewBulkSampleDetails: FC<ViewBulkSampleDetailsProps> = ({
  studyId,
  studyApiId,
  subjects,
  sampleDetails,
  metadata = [],
  closeModal,
  handleCallback,
}) => {
  const { plannedSamplesEnabled } = useSelector(featureSelector);

  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [tableReady, setTableReady] = useState<boolean>(false);
  const dataRef = useRef<Array<SampleData>>([]);

  const { invoke: createSamples, loading: creatingSamples } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyId}/samples/bulk',
    options: { invokeOnInit: false, onError: { slug: 'samples-bulk-create', toast: true, throw: false } },
  });

  const formatEncoder = formatColumnSettingsDataValue;

  const formatMetadata = (meta: Record<string, string>) =>
    Object.entries(meta).reduce<Array<MetadataPostItem>>((acc, [key, value]) => {
      const glossaryDetails = metadata.find(({ title }) => formatEncoder(title) === key);
      let formattedValue: string | string[];
      if (_notNil(glossaryDetails) && _isNotEmpty(value)) {
        if (glossaryDetails.field_type === 'date') {
          formattedValue = value;
        } else if (glossaryDetails.field_type === 'multi_select') {
          formattedValue = (value as string).split(',');
        } else {
          formattedValue = renderMetadataValue({
            value,
            field_type: glossaryDetails.field_type,
          }).toString();
        }

        acc.push({
          glossary_id: glossaryDetails.id,
          value: formattedValue,
        });
      }
      return acc;
    }, []);

  const resolveTimepoint = (timepoint: unknown): string | undefined => {
    if (Array.isArray(timepoint)) {
      return _isNotEmpty(timepoint) ? String(timepoint[0]) : undefined;
    }
    if (typeof timepoint === 'string' && _isNotBlank(timepoint)) {
      return timepoint;
    }
    return undefined;
  };

  const mapSampleItems = () =>
    dataRef.current.map(({ subject_id, subject_api_id, date, comments, meta, details, timepoint }) => {
      const formattedMeta = formatMetadata(meta ?? {});
      const resolvedTimepoint = resolveTimepoint(timepoint);
      return {
        subject_id,
        subject_api_id,
        date,
        comments,
        details: Object.entries(details).reduce<Array<SampleDetailV1>>((acc, [key, value]) => {
          if (_notNil(value) && _isNotEmpty(value)) {
            acc.push({
              key: key as SampleDetailKey,
              value: value as SampleDetailValue,
              unit: sampleDetails?.details?.[key as SampleMeasurementKey]?.unit ?? ('' as SampleDetailUnit),
            });
          }
          return acc;
        }, []),
        timepoint: _notNil(resolvedTimepoint) ? resolvedTimepoint : undefined,
        metadata: formattedMeta.length > 0 ? formattedMeta : undefined,
      };
    });

  const handleSave = async (finished: boolean) => {
    const animalCount = dataRef.current.length;
    const toastContent = (
      <div>
        Samples collected from {animalCount} animal{Number(animalCount) !== 1 ? 's. ' : '. '}
        <BrowserRouter>
          <Link
            to={web('studies.samples', { id: studyId })}
            className="link"
            onClick={() => handleCallback(finished, true)}
          >
            View Samples
          </Link>
        </BrowserRouter>
      </div>
    );

    const response = await createSamples({
      path: { studyId: studyApiId },
      body: {
        team_glossary_api_id: sampleDetails.sample_type.api_id,
        samples: mapSampleItems().map(({ metadata, ...rest }) => ({
          ...rest,
          metadata: metadata?.reduce<Array<{ glossary_id: number; value: string | number | boolean }>>(
            (acc, { glossary_id, value }) => {
              if (typeof glossary_id === 'number' && _notNil(value)) {
                acc.push({
                  glossary_id,
                  value: Array.isArray(value) ? JSON.stringify(value) : (value as string | number | boolean),
                });
              }
              return acc;
            },
            []
          ),
        })),
      },
    });
    if (response.type === 'success') {
      successToast(toastContent);
      handleCallback(finished, false);
    }
  };

  return (
    <ModalContainer size="wide" className="bg-white">
      <div className="flex flex-column pa3">
        <ModalHeader title="Samples details" closeModal={closeModal} className="w-100" />
        <p className="lh-copy f6">
          Review and amend samples and their associated data.
          <a
            href={RouteService.help('bulk_sample_collection')}
            target="_blank"
            rel="noopener noreferrer"
            className="link blue ml1"
          >
            Read more
          </a>
        </p>
      </div>
      <div
        data-testid="sample-details-container"
        data-test-table-ready={tableReady ? 'true' : 'false'}
        className={`ow-spreadsheet-styles mr0 ${styles.container}`}
      >
        <ViewBulkSampleDetailsTable
          subjects={subjects}
          metadata={metadata}
          setInvalid={setIsInvalid}
          sampleDetails={sampleDetails}
          plannedSamplesEnabled={plannedSamplesEnabled}
          dataRef={dataRef}
          onReady={setTableReady}
        />
      </div>
      <div className="flex g3 items-center w-100 pa3">
        <ModalActions
          submitBtnText="Save and finish"
          submitButtonProps={{
            className: 'ml3',
            disabled: creatingSamples || isInvalid || !tableReady,
            tooltip: isInvalid ? spreadSheetValidationTooltip : undefined,
          }}
          onSubmit={() => handleSave(true)}
        />
        <Button
          disabled={creatingSamples || isInvalid || !tableReady}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
          soft
          onClick={() => handleSave(false)}
          testKey="bulk-sample-save-button"
        >
          Save and collect another
        </Button>
      </div>
    </ModalContainer>
  );
};

export default ViewBulkSampleDetails;
